import React, { useState, useCallback } from 'react';
import { useUserContext } from 'shared/contexts/UserContext';
import {
  Box, Typography, Chip, Divider, Paper, Stack, FormControl, Select,
  MenuItem, InputLabel, Switch, FormControlLabel, Alert, IconButton, Tooltip,
  Collapse, Button,
} from '@mui/material';
import { ExpandMore, ExpandLess, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSnackbar } from 'shared/contexts/SnackbarContext';

import InvitationNotificationCard from 'components/comm/notifications/invitation/InvitationNotificationCard';
import JoinRequestNotificationCard from 'components/comm/notifications/invitation/JoinRequestNotificationCard';
import { SuggestionNotificationCard } from 'components/comm/notifications/invitation/SuggestionNotificationCard';
import MeetingRequestNotificationCard from 'components/comm/notifications/meeting/MeetingRequestNotificationCard';
import CommentNotificationCard from 'components/comm/notifications/comment/CommentNotificationCard';
import LikeNotificationCard from 'components/comm/notifications/like/LikeNotificationCard';
import FollowNotificationCard from 'components/comm/notifications/follow/FollowNotificationCard';
import VerificationBonusNotificationCard from 'components/comm/notifications/bonus/VerificationBonusNotificationCard';
import RoleAssignedNotificationCard from 'components/comm/notifications/proposal/RoleAssignedNotificationCard';
import ProposalChatNotificationCard from 'components/comm/notifications/proposal/ProposalChatNotificationCard';
import TaskAssignedNotificationCard from 'components/comm/notifications/proposal/TaskAssignedNotificationCard';
import TodoAssignedNotificationCard from 'components/comm/notifications/proposal/TodoAssignedNotificationCard';
import ProposalReinviteNotificationCard from 'components/comm/notifications/proposal/ProposalReinviteNotificationCard';
import ModerationWarningNotificationCard from 'components/comm/notifications/system/ModerationWarningNotificationCard';
import ModerationReportNotificationCard from 'components/comm/notifications/system/ModerationReportNotificationCard';

const UUID1 = '550e8400-e29b-41d4-a716-446655440001';
const UUID2 = '550e8400-e29b-41d4-a716-446655440002';
const UUID3 = '550e8400-e29b-41d4-a716-446655440003';
const UUID4 = '550e8400-e29b-41d4-a716-446655440004';

const mockNotification = (ntype, payload, overrides = {}) => ({
  id: UUID1,
  ntype,
  payload,
  is_read: false,
  created: new Date(Date.now() - 90000).toISOString(),
  ...overrides,
});

// ---------- Status options per card type ----------
const INVITATION_STATES = [
  { value: 'pending_recipient', label: 'Pending (as recipient) — shows Accept/Decline' },
  { value: 'accepted_recipient', label: 'Accepted (as recipient)' },
  { value: 'pending_inviter', label: 'Pending (as inviter)' },
  { value: 'accepted_inviter', label: 'Accepted (as inviter)' },
];

const JOIN_STATES = [
  { value: 'pending', label: 'Pending — shows Accept/Reject' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
];

const READ_STATES = [
  { value: false, label: 'Unread' },
  { value: true, label: 'Read' },
];

// Build invitation payload from state key.
// Inviter perspective: from_user = me (current user), to_user = Alice.
// Recipient perspective: from_user = Alice (who invited me), to_user = me.
function invitationPayload(stateKey, currentUser) {
  const isRecipient = stateKey.includes('recipient');
  const isPending = stateKey.includes('pending');
  const myName = currentUser?.name || currentUser?.username || 'You';
  const myAvatar = currentUser?.avatar || currentUser?.avatar_url || null;
  const action = isPending ? 'pending' : 'accepted';

  if (isRecipient) {
    return {
      consortium_invitation_id: UUID2,
      consortium_id: UUID3,
      consortium_name: 'NEXUS Research Consortium',
      from_user_id: UUID4,
      from_user_name: 'Alice Müller',
      from_user_avatar_url: null,
      to_user_name: myName,
      recipient_name: myName,
      role: 'recipient',
      action,
      invitation_status: action,
    };
  }
  return {
    consortium_invitation_id: UUID2,
    consortium_id: UUID3,
    consortium_name: 'NEXUS Research Consortium',
    from_user_id: currentUser?.id || UUID4,
    from_user_name: myName,
    from_user_avatar_url: myAvatar,
    to_user_name: 'Bob Smith',
    recipient_name: 'Bob Smith',
    role: 'inviter',
    action,
    invitation_status: action,
  };
}

function joinRequestPayload(status) {
  return {
    join_request_id: UUID2,
    consortium_id: UUID3,
    consortium_name: 'NEXUS Research Consortium',
    requester_id: UUID4,
    requester_name: 'Bob Smith',
    requester_avatar_url: null,
    status,
  };
}

// -------- Card section wrapper --------
function CardSection({ label, ntype, email, children, controls, lastAction }) {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden', borderRadius: 2 }}>
      {/* Header */}
      <Box
        sx={{
          px: 2, py: 1,
          bgcolor: 'action.hover',
          display: 'flex', alignItems: 'center', gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setCollapsed(c => !c)}
      >
        <Chip
          label={ntype}
          size="small"
          variant="outlined"
          sx={{ fontFamily: 'monospace', fontSize: '0.7rem', borderRadius: 1 }}
        />
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          📧 {email}
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {collapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={!collapsed}>
        {/* Card render area */}
        <Box sx={{ bgcolor: 'background.paper' }}>
          {children}
        </Box>

        {/* Controls */}
        {controls && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1.25, bgcolor: 'action.hover' }}>
              {controls}
            </Box>
          </>
        )}

        {/* Action log */}
        {lastAction && (
          <Box sx={{ px: 2, py: 0.75, bgcolor: 'success.light', borderTop: '1px solid', borderColor: 'success.light' }}>
            <Typography variant="caption" color="success.dark" sx={{ fontFamily: 'monospace' }}>
              ↳ {lastAction}
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

// -------- Main Page --------
export default function NotificationTestPage() {
  const { showSnackbar } = useSnackbar();
  const { user: currentUser } = useUserContext();

  // Per-card state
  const [invitationState, setInvitationState] = useState('pending_recipient');
  const [updateState, setUpdateState] = useState('pending_recipient');
  const [joinState, setJoinState] = useState('pending');
  const [isRead, setIsRead] = useState(false);
  const [actionLogs, setActionLogs] = useState({});
  const [resetKey, setResetKey] = useState(0);

  const logAction = useCallback((cardId, action) => {
    const msg = `action="${action.action}" meta=${JSON.stringify(action.meta ?? {})}`;
    setActionLogs(prev => ({ ...prev, [cardId]: `${new Date().toLocaleTimeString()} — ${msg}` }));
    showSnackbar(`[TEST] ${action.action}`, 'info');
  }, [showSnackbar]);

  // Mock action handler — logs but doesn't call API
  const makeMock = (cardId) => async (_notifId, action) => {
    logAction(cardId, action);
  };

  const noop = () => {};

  const resetAll = () => {
    setActionLogs({});
    setResetKey(k => k + 1);
  };

  const commonProps = (cardId) => ({
    key: `${cardId}-${resetKey}`,
    onMarkRead: noop,
    handleNotificationAction: makeMock(cardId),
  });

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto', py: 4, px: 2 }}>
      {/* Page header */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
        <Typography variant="h5" fontWeight={700}>
          Notification Cards · Test Bench
        </Typography>
        <Tooltip title="Reset all action logs">
          <IconButton size="small" onClick={resetAll}><RefreshIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Stack>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        Actions here are <strong>mocked</strong> — they log to the card footer without calling the API.
        To test live API actions (e.g. Rejoin), use the Notification menu in the app.
      </Alert>

      {/* Global read toggle */}
      <Stack direction="row" spacing={2} mb={3} alignItems="center">
        <FormControlLabel
          control={<Switch checked={isRead} onChange={e => setIsRead(e.target.checked)} size="small" />}
          label={<Typography variant="body2">Mark all cards as read</Typography>}
        />
      </Stack>

      {/* ---- consortium_invitation ---- */}
      <CardSection
        label="Consortium Invitation"
        ntype="consortium_invitation"
        email="alice.muller@university.edu"
        lastAction={actionLogs['invitation']}
        controls={
          <FormControl size="small" fullWidth>
            <InputLabel>State</InputLabel>
            <Select value={invitationState} label="State" onChange={e => setInvitationState(e.target.value)}>
              {INVITATION_STATES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
        }
      >
        <InvitationNotificationCard
          notification={mockNotification('consortium_invitation', invitationPayload(invitationState, currentUser), { is_read: isRead })}
          {...commonProps('invitation')}
        />
      </CardSection>

      {/* ---- consortium_update ---- */}
      <CardSection
        label="Consortium Update"
        ntype="consortium_update"
        email="alice.muller@university.edu"
        lastAction={actionLogs['update']}
        controls={
          <FormControl size="small" fullWidth>
            <InputLabel>State</InputLabel>
            <Select value={updateState} label="State" onChange={e => setUpdateState(e.target.value)}>
              {INVITATION_STATES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
        }
      >
        <InvitationNotificationCard
          notification={mockNotification('consortium_update', invitationPayload(updateState, currentUser), { is_read: isRead })}
          {...commonProps('update')}
        />
      </CardSection>

      {/* ---- consortium_join_request ---- */}
      <CardSection
        label="Join Request"
        ntype="consortium_join_request"
        email="bob.smith@institute.org"
        lastAction={actionLogs['join']}
        controls={
          <FormControl size="small" fullWidth>
            <InputLabel>Status</InputLabel>
            <Select value={joinState} label="Status" onChange={e => setJoinState(e.target.value)}>
              {JOIN_STATES.map(s => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </Select>
          </FormControl>
        }
      >
        <JoinRequestNotificationCard
          notification={mockNotification('consortium_join_request', joinRequestPayload(joinState), { is_read: isRead })}
          {...commonProps('join')}
        />
      </CardSection>

      {/* ---- proposal_reinvite ---- */}
      <CardSection
        label="Proposal Re-invite"
        ntype="proposal_reinvite"
        email="kemal.sanli@istanbul.edu.tr"
        lastAction={actionLogs['reinvite']}
      >
        <ProposalReinviteNotificationCard
          notification={mockNotification('proposal_reinvite', {
            role_id: UUID2,
            consortium_id: UUID3,
            consortium_name: 'NEXUS Research Consortium',
            from_user_id: currentUser?.id || UUID4,
            from_user_name: currentUser?.name || 'Kemal Sanli',
            from_user_avatar: currentUser?.avatar || currentUser?.avatar_url || null,
          }, { is_read: isRead })}
          {...commonProps('reinvite')}
        />
      </CardSection>

      {/* ---- suggestion_received ---- */}
      <CardSection
        label="Suggestion Received"
        ntype="suggestion_received"
        email="david.chen@univ.ac.uk"
        lastAction={actionLogs['suggestion']}
      >
        <SuggestionNotificationCard
          notification={mockNotification('suggestion_received', {
            suggestion_id: UUID2,
            author_id: UUID4,
            author_name: 'David Chen',
            author_avatar_url: null,
            target_name: 'Horizon Europe CL4-2024',
            target_type: 'call',
            body_preview: 'This call matches your research on photonics.',
            recipient_name: currentUser?.name || currentUser?.username || 'You',
          }, { is_read: isRead })}
          {...commonProps('suggestion')}
        />
      </CardSection>

      {/* ---- meeting_request ---- */}
      <CardSection
        label="Meeting Request"
        ntype="meeting_request"
        email="eve.taylor@consortium.eu"
        lastAction={actionLogs['meeting']}
      >
        <MeetingRequestNotificationCard
          notification={mockNotification('meeting_request', {
            meeting_request_id: UUID2,
            title: 'Kickoff Meeting — NEXUS Q1',
            description: 'Initial consortium kickoff to align on workpackages.',
            from_user_name: 'Eve Taylor',
            from_user_avatar: null,
            sender_avatar: null,
            poll_title: 'When can you meet?',
          }, { is_read: isRead })}
          onMarkAsRead={noop}
          handleNotificationAction={makeMock('meeting')}
        />
      </CardSection>

      {/* ---- comment ---- */}
      <CardSection
        label="Comment"
        ntype="comment"
        email="frank.wu@research.de"
      >
        <CommentNotificationCard
          notification={mockNotification('comment', {
            author_id: UUID4,
            author_name: 'Frank Wu',
            author_avatar_url: null,
            target_type: 'post',
            target_id: UUID3,
            body_preview: 'Great point about the methodology section!',
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- like ---- */}
      <CardSection
        label="Like"
        ntype="like"
        email="kemal.sanli@istanbul.edu.tr"
      >
        <LikeNotificationCard
          notification={mockNotification('like', {
            liker: {
              id: currentUser?.id || UUID4,
              name: currentUser?.name || 'Kemal Sanli',
              avatar: currentUser?.avatar || currentUser?.avatar_url || null,
            },
            content_type: 'post',
            content_id: UUID3,
            post_author_id: UUID4,
            reaction_type: '❤️',
            body_preview: 'Our photonics paper was just cited in the Nature Photonics review...',
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- follow ---- */}
      <CardSection
        label="Follow"
        ntype="follow"
        email="kemal.sanli@istanbul.edu.tr"
      >
        <FollowNotificationCard
          notification={mockNotification('follow', {
            follower: {
              id: currentUser?.id || UUID4,
              name: currentUser?.name || 'Kemal Sanli',
              avatar: currentUser?.avatar || currentUser?.avatar_url || null,
            },
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- role_assigned ---- */}
      <CardSection
        label="Role Assigned"
        ntype="role_assigned"
        email="system@otable.app"
      >
        <RoleAssignedNotificationCard
          notification={mockNotification('role_assigned', {
            consortium_id: UUID3,
            consortium_name: 'NEXUS Research Consortium',
            role: 'wp_leader',
            role_label: 'WP Leader',
            assigner_name: currentUser?.name || 'Kemal Sanli',
            assigner_avatar: currentUser?.avatar || currentUser?.avatar_url || null,
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- proposal_chat ---- */}
      <CardSection
        label="Proposal Chat"
        ntype="proposal_chat"
        email="kemal.sanli@istanbul.edu.tr"
      >
        <ProposalChatNotificationCard
          notification={mockNotification('proposal_chat', {
            consortium_id: UUID3,
            proposal_id: UUID2,
            proposal_acronym: 'NEXUS-I5',
            author_name: currentUser?.name || 'Kemal Sanli',
            author_avatar: currentUser?.avatar || currentUser?.avatar_url || null,
            body_preview: 'Can we revisit the budget allocation for WP3?',
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- task_assigned ---- */}
      <CardSection
        label="Task Assigned"
        ntype="task_assigned"
        email="system@otable.app"
      >
        <TaskAssignedNotificationCard
          notification={mockNotification('task_assigned', {
            proposal_id: UUID2,
            proposal_acronym: 'NEXUS-I5',
            task_title: 'Draft the Ethics Statement (WP2)',
            assigner_name: currentUser?.name || 'Kemal Sanli',
            assigner_avatar: currentUser?.avatar || currentUser?.avatar_url || null,
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- todo_assigned ---- */}
      <CardSection
        label="To-do Assigned"
        ntype="todo_assigned"
        email="system@otable.app"
      >
        <TodoAssignedNotificationCard
          notification={mockNotification('todo_assigned', {
            proposal_id: UUID2,
            proposal_acronym: 'NEXUS-I5',
            todo_title: 'Upload signed partnership agreement',
            priority: 'high',
            assigner_name: currentUser?.name || 'Kemal Sanli',
            assigner_avatar: currentUser?.avatar || currentUser?.avatar_url || null,
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- verification_bonus ---- */}
      <CardSection
        label="Verification Bonus"
        ntype="verification_bonus"
        email="system@otable.app"
      >
        <VerificationBonusNotificationCard
          notification={mockNotification('verification_bonus', {
            points: 50,
            reason: 'Profile verified by consortium coordinator',
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- moderation_warning ---- */}
      <CardSection
        label="Moderation Warning"
        ntype="moderation_warning"
        email="moderation@otable.app"
      >
        <ModerationWarningNotificationCard
          notification={mockNotification('moderation_warning', {
            target_type: 'call',
            target_id: UUID2,
            reason: 'Your comment was flagged as off-topic.',
            severity: 'warning',
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

      {/* ---- moderation_report ---- */}
      <CardSection
        label="Moderation Report"
        ntype="moderation_report"
        email="moderation@otable.app"
      >
        <ModerationReportNotificationCard
          notification={mockNotification('moderation_report', {
            target_type: 'call',
            target_id: UUID2,
            reporter_name: 'System',
            reason: 'Reported content violates community guidelines.',
          }, { is_read: isRead })}
          onMarkRead={noop}
        />
      </CardSection>

    </Box>
  );
}
